import {
  Content,
  ContentMetadata,
  ContentNode,
  ContentTypeMetadata,
} from '@amzn/awscat-pattern-library-service-model';

import { deepClone } from '../api/UtilsApi';

/**
 * Re-applies `Content`, `ContentNode`, `ContentMetadata`, and
 * `ContentTypeMetadata` prototypes onto a structurally-cloned tree. The
 * raw clone (via `rfdc`/`structuredClone`/`JSON.parse`) produces plain
 * objects whose prototype is `Object`; without this restoration,
 * `json2typescript`'s `serialize` walks the prototype chain looking for
 * `@JsonProperty` decorators, finds none, and emits `{}` — that's what
 * caused server payloads to mysteriously drop their bodies after a
 * client-side edit.
 */
function restoreContentNodePrototypes(node: ContentNode): void {
  Object.setPrototypeOf(node, ContentNode.prototype);
  if (Array.isArray(node.subContent)) {
    for (const child of node.subContent) restoreContentNodePrototypes(child);
  }
  if (Array.isArray(node.contentSections)) {
    for (const section of node.contentSections) restoreContentNodePrototypes(section);
  }
}

/**
 * Returns a deep clone of `content` whose nested model objects keep
 * their concrete class prototypes — `Content`, `ContentMetadata`,
 * `ContentTypeMetadata`, and every `ContentNode` in the tree. Use this
 * everywhere a v2 service or view model needs to copy-edit a Content;
 * a plain `deepClone` would strip prototypes and break downstream
 * `json2typescript` serialization.
 */
export function deepCloneContent(content: Content): Content {
  const cloned = deepClone(content) as Content;
  Object.setPrototypeOf(cloned, Content.prototype);
  if (cloned.contentMetadata) {
    Object.setPrototypeOf(cloned.contentMetadata, ContentMetadata.prototype);
  }
  if (cloned.contentTypeMetadata) {
    Object.setPrototypeOf(cloned.contentTypeMetadata, ContentTypeMetadata.prototype);
  }
  if (cloned.contentData) {
    restoreContentNodePrototypes(cloned.contentData);
  }
  return cloned;
}

/**
 * Returns a deep clone of a `ContentNode` subtree (e.g. the root
 * document node passed to page-reorder operations) with the
 * `ContentNode` prototype restored on every node.
 */
export function deepCloneContentNode(node: ContentNode): ContentNode {
  const cloned = deepClone(node) as ContentNode;
  restoreContentNodePrototypes(cloned);
  return cloned;
}
